import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Pencil } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'ID', label: 'ID Document' },
  { value: 'SERVICE_AGREEMENT', label: 'Service Agreement' },
  { value: 'CONSENT', label: 'Consent Form' },
  { value: 'POLICY', label: 'Policy' },
  { value: 'INCIDENT_DOC', label: 'Incident Document' },
  // Phase 2B (Feb 2026) — onboarding categories
  { value: 'NDIS_PLAN', label: 'NDIS Plan' },
  { value: 'BEHAVIOUR_SUPPORT_PLAN', label: 'Behaviour Support Plan' },
  { value: 'MEDICAL', label: 'Medical Document' },
  { value: 'GUARDIAN_DOCUMENT', label: 'Guardian Document' },
  { value: 'RISK_ASSESSMENT_DOC', label: 'Risk Assessment' },
  { value: 'OTHER', label: 'Other' },
];

const SOURCE_CONTEXTS = [
  { value: 'manual', label: 'Manual Upload' },
  { value: 'shift', label: 'After Shift' },
  { value: 'appointment', label: 'After Appointment' },
  { value: 'visit', label: 'After Visit' },
  { value: 'incident', label: 'Incident Related' },
  { value: 'other', label: 'Other' },
];

export function DocumentEditModal({ doc, participantId, open, onClose, onComplete }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('OTHER');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceContext, setSourceContext] = useState('manual');
  const [contextNote, setContextNote] = useState('');
  const [staffCanDownload, setStaffCanDownload] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (doc && open) {
      setTitle(doc.title || '');
      setCategory(doc.category || 'OTHER');
      setExpiryDate(doc.expiryDate ? doc.expiryDate.split('T')[0] : '');
      setNotes(doc.notes || '');
      setSourceContext(doc.sourceContext || 'manual');
      setContextNote(doc.contextNote || '');
      setStaffCanDownload(!!doc.staffCanDownload);
    }
  }, [doc, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('title', title);
      params.append('category', category);
      if (expiryDate) params.append('expiryDate', expiryDate);
      else params.append('expiryDate', '');
      params.append('notes', notes);
      params.append('sourceContext', sourceContext);
      params.append('contextNote', contextNote);
      params.append('staffCanDownload', staffCanDownload ? 'true' : 'false');

      await api.put(`/participants/${participantId}/documents/${doc.id}?${params.toString()}`);
      toast.success('Document updated');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Pencil className="w-4 h-4" /> Edit Document
          </DialogTitle>
          <DialogDescription>Update document metadata.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 text-sm" data-testid="edit-title-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Document Type</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-8 text-sm" data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Source Context</Label>
              <Select value={sourceContext} onValueChange={setSourceContext}>
                <SelectTrigger className="h-8 text-sm" data-testid="edit-source-context"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_CONTEXTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {sourceContext !== 'manual' && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Context Note</Label>
              <Input value={contextNote} onChange={(e) => setContextNote(e.target.value)} className="h-8 text-sm" data-testid="edit-context-note" placeholder="e.g., Collected after GP visit..." />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs font-medium">Expiry Date</Label>
            <TrinitiDatePicker value={expiryDate} onChange={(v) => setExpiryDate(v)} className="h-8 text-sm" testId="edit-expiry-input" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes..." className="h-8 text-sm" data-testid="edit-notes-input" />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Checkbox id="editStaffDL" checked={staffCanDownload} onCheckedChange={setStaffCanDownload} data-testid="edit-staff-download-checkbox" />
            <Label htmlFor="editStaffDL" className="text-xs text-muted-foreground cursor-pointer">Allow support staff to download</Label>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="edit-submit-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
